<script lang="ts">
  import { untrack } from 'svelte';
  import type {
    JoinRuleView,
    RoomPermissionsView,
    RoomPowerLevelsView,
    RoomListItemView,
  } from '#src/generated/protocol';
  import DoorOpenIcon from 'phosphor-svelte/lib/DoorOpenIcon';
  import GlobeIcon from 'phosphor-svelte/lib/GlobeIcon';
  import HandIcon from 'phosphor-svelte/lib/HandIcon';
  import HouseIcon from 'phosphor-svelte/lib/HouseIcon';
  import LockIcon from 'phosphor-svelte/lib/LockIcon';

  import { useCoreClient } from '#lib/core/context.js';
  import { i18n } from '#lib/i18n.js';
  import { nativeFilePicker } from '#lib/platform/native-file-picker.js';
  import Alert from '#lib/ui/primitives/Alert.svelte';
  import Avatar from '#lib/ui/primitives/Avatar.svelte';
  import Button from '#lib/ui/primitives/Button.svelte';
  import FormField from '#lib/ui/primitives/FormField.svelte';
  import OptionCards from '#lib/ui/primitives/OptionCards.svelte';
  import SettingsRow from '#lib/ui/primitives/SettingsRow.svelte';
  import SettingsSection from '#lib/ui/primitives/SettingsSection.svelte';
  import TextArea from '#lib/ui/primitives/TextArea.svelte';
  import TextInput from '#lib/ui/primitives/TextInput.svelte';
  import { uprightJpeg } from '#lib/ui/upright-jpeg.js';

  import '#lib/ui/primitives/settings-row.css';

  import {
    bannerChanges,
    readRoomBanner,
    ROOM_BANNER_EVENT,
    setRoomBanner,
  } from '../room-banner.svelte.js';
  import RoomAddressSettings from './RoomAddressSettings.svelte';
  import RoomEncryptionSettings from './RoomEncryptionSettings.svelte';
  import RoomHistorySettings from './RoomHistorySettings.svelte';
  import RoomPublishSettings from './RoomPublishSettings.svelte';
  import RoomSpacesSettings from './RoomSpacesSettings.svelte';
  import RoomUpgradeSettings from './RoomUpgradeSettings.svelte';
  import { canSendState } from './permission-groups';
  import Banner from '#lib/ui/primitives/Banner.svelte';

  interface Props {
    room: RoomListItemView | null;
    permissions: RoomPermissionsView | null;
    levels: RoomPowerLevelsView | null;
    onClose: () => void;
  }

  let { room, permissions, levels, onClose }: Props = $props();
  const core = useCoreClient();

  let name = $derived.by(() => {
    void roomId;
    return untrack(() => room?.name ?? '');
  });
  let topicDraft = $derived.by(() => {
    void roomId;
    return untrack(() => topic);
  });
  let pendingRule = $derived.by<JoinRuleView | null>(() => {
    void roomId;
    return null;
  });
  let saving = $state(false);
  let saved = $derived.by(() => {
    void roomId;
    return false;
  });
  let failed = $derived.by(() => {
    void roomId;
    return false;
  });
  let avatarInput = $state<HTMLInputElement | null>(null);
  let bannerInput = $state<HTMLInputElement | null>(null);
  let banner = $state<string | null>(null);

  let roomId = $derived(room?.room_id ?? null);
  let topic = $derived(room?.topic ?? '');
  let hasSpaceParent = $derived(
    roomId === null
      ? false
      : await core.rpc.spaces.roomHasSpaceParent({ room_id: roomId }).catch((error: unknown) => {
          console.debug('[sable room] space parent check failed', error);
          return false;
        })
  );

  let settableRules = $derived.by(() => {
    const rules: JoinRuleView[] = ['public', 'invite'];
    if (room?.supports_knock) rules.push('knock');
    if (hasSpaceParent && room?.supports_restricted) rules.push('restricted');
    if (hasSpaceParent && room?.supports_knock_restricted) {
      rules.push('knock_restricted');
    }
    return rules;
  });
  let savedRule = $derived(settableRules.find((rule) => rule === room?.join_rule) ?? null);
  let joinRule = $derived<JoinRuleView | null>(pendingRule ?? savedRule);
  let unsettableRule = $derived(joinRule === null);
  let ownPowerLevel = $derived(permissions?.own_power_level ?? 0);
  let canEditName = $derived(canSendState(levels, ownPowerLevel, 'm.room.name'));
  let canEditTopic = $derived(canSendState(levels, ownPowerLevel, 'm.room.topic'));
  let canEditAvatar = $derived(canSendState(levels, ownPowerLevel, 'm.room.avatar'));
  let canEditBanner = $derived(canSendState(levels, ownPowerLevel, ROOM_BANNER_EVENT));
  let canEditGeneral = $derived(canEditName || canEditTopic);
  let canEditAccess = $derived(canSendState(levels, ownPowerLevel, 'm.room.join_rules'));
  let dirty = $derived(
    (canEditName && name !== (room?.name ?? '')) ||
      (canEditTopic && topicDraft !== topic) ||
      (pendingRule !== null && pendingRule !== savedRule)
  );

  async function run(action: () => Promise<void>): Promise<void> {
    saving = true;
    saved = false;
    failed = false;
    try {
      await action();
      saved = true;
    } catch (error) {
      console.warn('[sable room] settings change failed', error);
      failed = true;
    } finally {
      saving = false;
    }
  }

  async function save(): Promise<void> {
    const target = roomId;
    if (!target) return;
    await run(async () => {
      if (canEditName && name !== (room?.name ?? '')) {
        await core.rpc.room_state.setName({
          room_id: target,
          name: name.trim() === '' ? null : name.trim(),
        });
      }
      if (canEditTopic && topicDraft !== topic) {
        await core.rpc.room_state.setTopic({ room_id: target, topic: topicDraft.trim() });
      }
      if (pendingRule !== null && pendingRule !== savedRule) {
        await core.rpc.room_state.setJoinRule({ room_id: target, rule: pendingRule });
      }
    });
  }

  function resetDraft(): void {
    name = room?.name ?? '';
    topicDraft = topic;
    pendingRule = null;
    failed = false;
  }

  function selectJoinRule(rule: JoinRuleView): void {
    pendingRule = rule;
    saved = false;
  }

  function convertToGroup(): void {
    const target = roomId;
    if (!target) return;
    void run(async () => {
      await core.rpc.account.setDirect({ room_id: target, direct: false, user_id: null });
    });
  }

  function resetDirectName(): void {
    const target = roomId;
    if (!target || !canEditName) return;
    void run(async () => {
      await core.rpc.room_state.setName({ room_id: target, name: null });
      name = '';
    });
  }

  async function uploadAvatar(event: Event & { currentTarget: HTMLInputElement }): Promise<void> {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    const target = roomId;
    if (!file || !target || !canEditAvatar) return;

    await run(async () => {
      const upright = await uprightJpeg(file);
      const bytes = new Uint8Array(await upright.arrayBuffer());
      await core.uploadRoomAvatar(target, upright.type || 'image/*', bytes);
    });
  }

  function removeAvatar(): void {
    const target = roomId;
    if (!target || !canEditAvatar) return;
    void run(async () => {
      await core.rpc.room_state.setAvatar({ room_id: target, url: null });
    });
  }

  $effect(() => {
    const target = roomId;
    void bannerChanges.version;
    if (!target) {
      banner = null;
      return;
    }

    let current = true;
    void readRoomBanner(core, target).then((next) => {
      if (current) banner = next;
    });
    return () => {
      current = false;
    };
  });

  async function uploadBanner(event: Event & { currentTarget: HTMLInputElement }): Promise<void> {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    const target = roomId;
    if (!file || !target || !canEditBanner) return;

    await run(async () => {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const uri = await core.media.uploadMedia(file.type || 'image/*', bytes);
      await setRoomBanner(core, target, uri);
    });
  }

  function removeBanner(): void {
    const target = roomId;
    if (!target || !canEditBanner) return;
    void run(async () => {
      await setRoomBanner(core, target, null);
    });
  }
</script>

{#snippet bannerPreview()}
  {#if banner}
    <div class="banner-preview">
      <Banner source={banner} {name} width={160} height={90} />
    </div>
  {/if}
{/snippet}

<div class="section">
  <SettingsSection
    headingId="room-settings-general"
    title={i18n.t('room.settingsGeneral')}
    description={i18n.t('room.settingsGeneralDescription')}
  >
    <ul class="settings-rows">
      <SettingsRow
        title={i18n.t('room.settingsAvatarLabel')}
        description={i18n.t('room.settingsAvatarHint')}
      >
        {#snippet before()}
          <Avatar
            id={roomId}
            src={room?.avatar_url ?? null}
            openable={true}
            name={room?.name ?? ''}
          />
        {/snippet}
        {#if canEditAvatar}
          <Button size="small" disabled={saving} onclick={() => avatarInput?.click()}>
            {i18n.t('common.changeAvatar')}
          </Button>
          {#if room?.avatar_url}
            <Button size="small" variant="ghost" disabled={saving} onclick={removeAvatar}>
              {i18n.t('common.removeAvatar')}
            </Button>
          {/if}
        {/if}
        <input
          bind:this={avatarInput}
          use:nativeFilePicker
          class="avatar-input"
          type="file"
          accept="image/*"
          tabindex="-1"
          aria-hidden="true"
          onchange={uploadAvatar}
        />
      </SettingsRow>
      <SettingsRow
        title={i18n.t('room.settingsBannerLabel')}
        description={i18n.t('room.settingsBannerHint')}
        before={banner ? bannerPreview : undefined}
      >
        {#if canEditBanner}
          <Button size="small" disabled={saving} onclick={() => bannerInput?.click()}>
            {i18n.t('common.changeBanner')}
          </Button>
          {#if banner}
            <Button size="small" variant="ghost" disabled={saving} onclick={removeBanner}>
              {i18n.t('common.removeBanner')}
            </Button>
          {/if}
        {/if}
        <input
          bind:this={bannerInput}
          use:nativeFilePicker
          class="avatar-input"
          type="file"
          accept="image/*"
          tabindex="-1"
          aria-hidden="true"
          onchange={uploadBanner}
        />
      </SettingsRow>
    </ul>
    {#if canEditGeneral}
      <div class="settings-form">
        <FormField fieldId="room-settings-name" label={i18n.t('room.settingsNameLabel')}>
          <TextInput id="room-settings-name" bind:value={name} readonly={!canEditName} />
        </FormField>
        <FormField fieldId="room-settings-topic" label={i18n.t('room.settingsTopicLabel')}>
          <TextArea id="room-settings-topic" bind:value={topicDraft} readonly={!canEditTopic} />
        </FormField>
      </div>
    {:else}
      <ul class="settings-rows">
        <SettingsRow title={i18n.t('room.settingsNameLabel')} description={room?.name ?? ''} />
        {#if topic !== ''}
          <SettingsRow title={i18n.t('room.settingsTopicLabel')} description={topic} />
        {/if}
      </ul>
    {/if}
  </SettingsSection>

  <SettingsSection
    headingId="room-settings-access"
    title={i18n.t('room.settingsAccess')}
    description={i18n.t('room.settingsAccessDescription')}
  >
    <div class="settings-form">
      {#if unsettableRule && canEditAccess}
        <Alert variant="warning" role="status">
          {i18n.t('room.settingsJoinRuleUnsettable', {
            rule: i18n.t(`room.joinRule.${room?.join_rule ?? 'unknown'}`),
          })}
        </Alert>
      {/if}
      {#if canEditAccess}
        <OptionCards
          label={i18n.t('room.settingsAccess')}
          value={joinRule}
          disabled={saving}
          onSelect={selectJoinRule}
          options={[
            {
              value: 'public',
              label: i18n.t('room.settingsJoinRulePublic'),
              hint: i18n.t('room.settingsJoinRulePublicHint'),
              icon: GlobeIcon,
            },
            {
              value: 'invite',
              label: i18n.t('room.createJoinRuleInvite'),
              hint: i18n.t('room.settingsJoinRuleInviteHint'),
              icon: LockIcon,
            },
            ...(room?.supports_knock
              ? [
                  {
                    value: 'knock' as const,
                    label: i18n.t('common.askToJoin'),
                    hint: i18n.t('room.settingsJoinRuleKnockHint'),
                    icon: HandIcon,
                  },
                ]
              : []),
            ...(hasSpaceParent && room?.supports_restricted
              ? [
                  {
                    value: 'restricted' as const,
                    label: i18n.t('room.createJoinRuleRestricted'),
                    hint: i18n.t('room.settingsJoinRuleRestrictedHint'),
                    icon: HouseIcon,
                  },
                ]
              : []),
            ...(hasSpaceParent && room?.supports_knock_restricted
              ? [
                  {
                    value: 'knock_restricted' as const,
                    label: i18n.t('room.settingsJoinRuleKnockRestricted'),
                    hint: i18n.t('room.settingsJoinRuleKnockRestrictedHint'),
                    icon: DoorOpenIcon,
                  },
                ]
              : []),
          ]}
        />
      {:else}
        <p class="read-only-value">
          {i18n.t(`room.joinRule.${room?.join_rule ?? 'unknown'}`)}
        </p>
      {/if}
    </div>

    <ul class="settings-rows">
      {#if room?.room_kind !== 'space'}
        <RoomHistorySettings {room} {levels} {ownPowerLevel} />
        <RoomEncryptionSettings {room} {levels} {ownPowerLevel} />
      {/if}
      <RoomPublishSettings {room} {levels} {ownPowerLevel} />
      <RoomSpacesSettings {room} {levels} {ownPowerLevel} />
      {#if room?.is_direct}
        <SettingsRow
          title={i18n.t('common.directMessage')}
          description={i18n.t('room.settingsDirectHint')}
        >
          {#if canEditName && room.name}
            <Button size="small" variant="ghost" disabled={saving} onclick={resetDirectName}>
              {i18n.t('room.settingsDirectResetName')}
            </Button>
          {/if}
          <Button size="small" disabled={saving} onclick={convertToGroup}>
            {i18n.t('room.menuConvertToGroup')}
          </Button>
        </SettingsRow>
      {/if}
    </ul>
  </SettingsSection>

  <RoomAddressSettings {room} {levels} {ownPowerLevel} />

  <RoomUpgradeSettings {room} {levels} {ownPowerLevel} {onClose} />

  {#if canEditGeneral || canEditAccess}
    <div class={['save-bar', { pending: dirty }]}>
      {#if failed}
        <p class="save-status error" role="alert">{i18n.t('room.settingsFailed')}</p>
      {:else if dirty}
        <p class="save-status" role="status">{i18n.t('common.unsavedChangesNotice')}</p>
      {:else if saved}
        <p class="save-status" role="status">{i18n.t('room.settingsSaved')}</p>
      {/if}
      {#if dirty}
        <Button variant="ghost" disabled={saving} onclick={resetDraft}>
          {i18n.t('common.reset')}
        </Button>
      {/if}
      <Button variant="primary" disabled={!dirty || saving} loading={saving} onclick={save}>
        {i18n.t('common.save')}
      </Button>
    </div>
  {/if}
</div>

<style>
  .section {
    display: grid;
    gap: var(--space-600);
  }

  .read-only-value {
    color: var(--surface-var-on-container);
    font-size: var(--font-size-small);
    margin: 0;
  }

  .section .banner-preview :global(.media-image) {
    border-radius: var(--radius);
    flex: none;
    height: 2.5rem;
    max-width: none;
    object-fit: cover;
    overflow: hidden;
  }

  .avatar-input {
    height: 0;
    opacity: 0;
    position: absolute;
    width: 0;
  }

  .save-bar {
    align-items: center;
    border: var(--border-width) solid transparent;
    border-radius: var(--radius);
    display: flex;
    gap: var(--space-300);
    justify-content: flex-end;
    padding: var(--space-200) var(--space-300);
  }

  .save-bar.pending {
    background: var(--bg-container);
    border-color: var(--bg-container-line);
    bottom: var(--space-300);
    box-shadow: var(--shadow-e200);
    color: var(--bg-on-container);
    position: sticky;
    z-index: 1;
  }

  .save-status {
    color: var(--surface-var-on-container);
    font-size: var(--font-size-small);
    margin: 0;
    margin-right: auto;
  }

  .save-bar.pending .save-status:not(.error) {
    color: var(--bg-on-container);
    font-weight: var(--font-weight-medium);
  }

  .save-status.error {
    color: var(--crit-main);
  }
</style>
